import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { listLeagues, listOrganizations, listPlayerParents, listPlayers, listTeams, listUserAssignments, listUsers as listAppUsers } from "../graphql/queries";
import { createUser, updateUser } from "../graphql/mutations";
import AppDialog from "../components/AppDialog";
import { formatApiError } from "../lib/formatApiError";
import { getTeamCoachUsernames, type UserAssignmentLike } from "../lib/relationshipScope";
import "./UserManagementPage.css";

type AppUser = {
  id: string;
  email: string;
  givenName?: string | null;
  familyName?: string | null;
  phone?: string | null;
  safetyCertified?: boolean | null;
  abcaId?: string | null;
  roles: string[];
};

type CognitoAttribute = {
  Name: string;
  Value: string;
};

type CognitoListUser = {
  Username: string;
  Enabled?: boolean;
  UserStatus?: string;
  Attributes?: CognitoAttribute[];
};

type CognitoAccountState = {
  username: string;
  email?: string;
  enabled?: boolean;
  userStatus?: string;
};

type UserProfileDraft = {
  givenName: string;
  familyName: string;
  phone: string;
  safetyCertified: boolean;
  abcaId: string;
};

type CreateUserDraft = UserProfileDraft & {
  username: string;
  email: string;
  roles: string[];
};

type Organization = {
  id: string;
  name: string;
  admins?: string[] | null;
};

type League = {
  id: string;
  name: string;
  admins?: string[] | null;
};

type Team = {
  id: string;
  name: string;
  coachUsernames?: string[] | null;
};

type Player = {
  id: string;
  name: string;
};

type PlayerParent = {
  id: string;
  playerId: string;
  parentEmail: string;
};

type LinkedObjects = {
  teams: Team[];
  organizations: Organization[];
  leagues: League[];
  players: Player[];
};

type UserAssignment = UserAssignmentLike;

const managedRoles = ["SuperAdmin", "Commish", "Coach", "Parent"] as const;
type ManagedRole = (typeof managedRoles)[number];
type UserTab = "All" | "Pending" | ManagedRole;
const userTabs: UserTab[] = ["All", "Pending", ...managedRoles];

const emptyProfileDraft = (): UserProfileDraft => ({
  givenName: "",
  familyName: "",
  phone: "",
  safetyCertified: false,
  abcaId: ""
});

const emptyCreateDraft = (): CreateUserDraft => ({
  username: "",
  email: "",
  givenName: "",
  familyName: "",
  phone: "",
  safetyCertified: false,
  abcaId: "",
  roles: ["Parent"]
});

const normalizeRoles = (roles: string[] | null | undefined): ManagedRole[] =>
  Array.from(new Set((roles ?? []).filter((role): role is ManagedRole => managedRoles.includes(role as ManagedRole))));

const getCognitoEmail = (user: CognitoListUser): string =>
  (user.Attributes?.find((attribute) => attribute.Name === "email")?.Value ?? "").trim().toLowerCase();

const getDisplayName = (user: AppUser, draft?: UserProfileDraft) => {
  const givenName = draft?.givenName ?? user.givenName;
  const familyName = draft?.familyName ?? user.familyName;
  const fullName = `${givenName ?? ""} ${familyName ?? ""}`.trim();
  return fullName || user.email || user.id;
};

const getPrimaryRole = (roles: string[]) => managedRoles.find((role) => roles.includes(role)) ?? "Parent";

const getStatusLabel = (state?: CognitoAccountState) => {
  if (!state) {
    return "Profile Only";
  }
  if (state.enabled === false) {
    return "Disabled";
  }
  if (!state.userStatus) {
    return "Unknown";
  }
  return state.userStatus.replace(/_/g, " ");
};

const summarizeNames = (items: Array<{ name: string }>, limit = 2) => {
  if (items.length === 0) {
    return "None";
  }
  const names = items.map((item) => item.name);
  if (names.length <= limit) {
    return names.join(", ");
  }
  return `${names.slice(0, limit).join(", ")} +${names.length - limit}`;
};

const getAdminAuthHeaders = async (preferIdToken = true) => {
  const session = await Auth.currentSession();
  const accessToken = session.getAccessToken().getJwtToken();
  const idToken = session.getIdToken().getJwtToken();
  return {
    Authorization: preferIdToken ? (idToken || accessToken) : (accessToken || idToken)
  };
};

const shouldRetryAdminQueries = (error: unknown) => {
  const message = formatApiError(error).toLowerCase();
  return (
    message.includes("network error") ||
    message.includes("not authorized") ||
    message.includes("unauthorized") ||
    message.includes("forbidden") ||
    message.includes("403") ||
    message.includes("401")
  );
};

const adminGet = async (path: string, queryStringParameters?: Record<string, unknown>) => {
  try {
    return await API.get("AdminQueries", path, {
      headers: await getAdminAuthHeaders(true),
      queryStringParameters
    });
  } catch (error) {
    if (!shouldRetryAdminQueries(error)) {
      throw error;
    }
    return API.get("AdminQueries", path, {
      headers: await getAdminAuthHeaders(false),
      queryStringParameters
    });
  }
};

const adminPost = async (path: string, body?: Record<string, unknown>) => {
  try {
    return await API.post("AdminQueries", path, {
      headers: await getAdminAuthHeaders(true),
      body
    });
  } catch (error) {
    if (!shouldRetryAdminQueries(error)) {
      throw error;
    }
    return API.post("AdminQueries", path, {
      headers: await getAdminAuthHeaders(false),
      body
    });
  }
};

const shouldIgnoreGroupSyncError = (error: unknown, action: "add" | "remove") => {
  const message = formatApiError(error).toLowerCase();
  if (action === "remove") {
    return message.includes("not found in group") || message.includes("user is not in group");
  }
  if (action === "add") {
    return message.includes("already") && message.includes("group");
  }
  return false;
};

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [appUsersById, setAppUsersById] = useState<Record<string, AppUser>>({});
  const [cognitoStateByUser, setCognitoStateByUser] = useState<Record<string, CognitoAccountState>>({});
  const [profileDrafts, setProfileDrafts] = useState<Record<string, UserProfileDraft>>({});
  const [selectedRoles, setSelectedRoles] = useState<Record<string, ManagedRole[]>>({});
  const [loading, setLoading] = useState(true);
  const [savingUser, setSavingUser] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerParents, setPlayerParents] = useState<PlayerParent[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [editingUsername, setEditingUsername] = useState<string | null>(null);
  const [detailUsername, setDetailUsername] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<CreateUserDraft>(emptyCreateDraft);
  const [activeTab, setActiveTab] = useState<UserTab>("All");

  const loadUsers = async () => {
    setLoading(true);
    setErrorMessage("");
    setWarningMessage("");

    try {
      let nextToken: string | null | undefined = null;
      const allAppUsers: AppUser[] = [];
      do {
        const response: any = await API.graphql(graphqlOperation(listAppUsers, { limit: 500, nextToken }));
        const page = ((response as any)?.data?.listUsers?.items ?? []).filter(Boolean) as AppUser[];
        allAppUsers.push(...page);
        nextToken = (response as any)?.data?.listUsers?.nextToken;
      } while (nextToken);

      const [orgRes, leagueRes, teamRes, playerRes, playerParentRes, userAssignmentRes] = await Promise.allSettled([
        API.graphql(graphqlOperation(listOrganizations, { limit: 1000 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeams, { limit: 1000 })),
        API.graphql(graphqlOperation(listPlayers, { limit: 3000 })),
        API.graphql(graphqlOperation(listPlayerParents, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 }))
      ]);

      setOrganizations(orgRes.status === "fulfilled" ? (((orgRes.value as any)?.data?.listOrganizations?.items ?? []).filter(Boolean) as Organization[]) : []);
      setLeagues(leagueRes.status === "fulfilled" ? (((leagueRes.value as any)?.data?.listLeagues?.items ?? []).filter(Boolean) as League[]) : []);
      setTeams(teamRes.status === "fulfilled" ? (((teamRes.value as any)?.data?.listTeams?.items ?? []).filter(Boolean) as Team[]) : []);
      setPlayers(playerRes.status === "fulfilled" ? (((playerRes.value as any)?.data?.listPlayers?.items ?? []).filter(Boolean) as Player[]) : []);
      setPlayerParents(
        playerParentRes.status === "fulfilled"
          ? (((playerParentRes.value as any)?.data?.listPlayerParents?.items ?? []).filter(Boolean) as PlayerParent[])
          : []
      );
      setUserAssignments(
        userAssignmentRes.status === "fulfilled"
          ? (((userAssignmentRes.value as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean) as UserAssignment[])
          : []
      );

      const sortedUsers = [...allAppUsers].sort((a, b) => {
        const aName = getDisplayName(a).toLowerCase();
        const bName = getDisplayName(b).toLowerCase();
        return aName.localeCompare(bName) || a.id.localeCompare(b.id);
      });
      const appById = sortedUsers.reduce<Record<string, AppUser>>((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {});

      const nextDrafts = sortedUsers.reduce<Record<string, UserProfileDraft>>((acc, user) => {
        acc[user.id] = {
          givenName: user.givenName ?? "",
          familyName: user.familyName ?? "",
          phone: user.phone ?? "",
          safetyCertified: Boolean(user.safetyCertified ?? false),
          abcaId: user.abcaId ?? ""
        };
        return acc;
      }, {});

      setUsers(sortedUsers);
      setAppUsersById(appById);
      setProfileDrafts(nextDrafts);

      try {
        const statesByUsername: Record<string, CognitoAccountState> = {};
        let adminToken: string | undefined;

        do {
          const response: any = await adminGet("/listUsers", {
            limit: 60,
            ...(adminToken ? { token: adminToken } : {})
          });
          const page: CognitoListUser[] = (response?.Users ?? []).filter(Boolean);
          for (const user of page) {
            statesByUsername[user.Username] = {
              username: user.Username,
              email: getCognitoEmail(user),
              enabled: user.Enabled,
              userStatus: user.UserStatus
            };
          }
          adminToken = response?.NextToken;
        } while (adminToken);

        setCognitoStateByUser(statesByUsername);
      } catch (error) {
        setCognitoStateByUser({});
        let debugSuffix = "";
        try {
          const debugResponse: any = await adminGet("/debugAdminAccess");
          const callerGroups = Array.isArray(debugResponse?.caller?.groups) ? debugResponse.caller.groups.join(", ") : "";
          const cognitoError = debugResponse?.cognito?.error ? ` | Cognito: ${debugResponse.cognito.error}` : "";
          debugSuffix = ` | Debug caller=${debugResponse?.caller?.username || "unknown"} groups=[${callerGroups}]${cognitoError}`;
        } catch {}
        setWarningMessage(`Loaded app user profiles, but Cognito account statuses could not be loaded: ${formatApiError(error)}${debugSuffix}`);
      }
    } catch (error) {
      setErrorMessage(`Failed to load users: ${formatApiError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const currentRolesByUser = useMemo(() => {
    const byUser: Record<string, ManagedRole[]> = {};
    for (const user of users) {
      const roles = normalizeRoles(appUsersById[user.id]?.roles ?? user.roles);
      byUser[user.id] = roles.length > 0 ? roles : ["Parent"];
    }
    return byUser;
  }, [appUsersById, users]);

  const cognitoStateByEmail = useMemo(() => {
    const out: Record<string, CognitoAccountState> = {};
    for (const user of Object.values(cognitoStateByUser)) {
      const email = (user.email ?? "").trim().toLowerCase();
      if (email && !out[email]) {
        out[email] = user;
      }
    }
    return out;
  }, [cognitoStateByUser]);

  const resolveCognitoState = (user: AppUser): CognitoAccountState | undefined =>
    cognitoStateByUser[user.id] ?? cognitoStateByEmail[(user.email ?? "").trim().toLowerCase()];

  const resolveCognitoUsername = (user: AppUser): string => resolveCognitoState(user)?.username ?? user.id;

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const user of users) {
      set.add(getStatusLabel(resolveCognitoState(user)));
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [cognitoStateByEmail, cognitoStateByUser, users]);

  const playerById = useMemo(() => {
    const out: Record<string, Player> = {};
    for (const player of players) {
      out[player.id] = player;
    }
    return out;
  }, [players]);

  const linkedObjectsByUser = useMemo(() => {
    const out: Record<string, LinkedObjects> = {};
    for (const user of users) {
      const username = user.id;
      const email = (user.email ?? "").toLowerCase();
      const linkedTeams = teams.filter((team) => getTeamCoachUsernames({ team, userAssignments }).includes(username));
      const linkedOrganizations = organizations.filter((org) =>
        (org.admins ?? []).includes(username) || userAssignments.some((assignment) => assignment.userId === username && assignment.organizationId === org.id)
      );
      const linkedLeagues = leagues.filter((league) =>
        (league.admins ?? []).includes(username) || userAssignments.some((assignment) => assignment.userId === username && assignment.leagueId === league.id)
      );
      const parentPlayerIds = new Set(
        playerParents
          .filter((link) => (link.parentEmail ?? "").toLowerCase() === email)
          .map((link) => link.playerId)
      );
      for (const assignment of userAssignments) {
        if (assignment.userId === username && assignment.playerId) {
          parentPlayerIds.add(assignment.playerId);
        }
      }
      const linkedPlayers = [...parentPlayerIds].map((id) => playerById[id]).filter(Boolean) as Player[];
      out[username] = {
        teams: linkedTeams,
        organizations: linkedOrganizations,
        leagues: linkedLeagues,
        players: linkedPlayers
      };
    }
    return out;
  }, [leagues, organizations, playerById, playerParents, teams, userAssignments, users]);

  const filteredUsers = useMemo(() => {
    const normalizedText = filterText.trim().toLowerCase();
    return [...users]
      .filter((user) => {
        const draft = profileDrafts[user.id];
        const roles = currentRolesByUser[user.id] ?? [];
        const role = getPrimaryRole(roles);
        const status = getStatusLabel(resolveCognitoState(user));
        const displayName = getDisplayName(user, draft);
        const haystack = [
          displayName,
          user.id,
          user.email,
          roles.join(" "),
          role,
          status,
          draft?.phone ?? "",
          draft?.abcaId ?? ""
        ]
          .join(" ")
          .toLowerCase();

        if (normalizedText && !haystack.includes(normalizedText)) {
          return false;
        }
        if (filterRole && !roles.includes(filterRole as ManagedRole)) {
          return false;
        }
        if (filterStatus && status !== filterStatus) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aName = getDisplayName(a, profileDrafts[a.id]).toLowerCase();
        const bName = getDisplayName(b, profileDrafts[b.id]).toLowerCase();
        return aName.localeCompare(bName) || a.id.localeCompare(b.id);
      });
  }, [cognitoStateByEmail, cognitoStateByUser, currentRolesByUser, filterRole, filterStatus, filterText, profileDrafts, users]);

  const usersByTab = useMemo(() => {
    const pendingUsers = filteredUsers.filter((user) => {
      const status = resolveCognitoState(user);
      return !status || status.enabled === false || status.userStatus !== "CONFIRMED";
    });
    return {
      All: filteredUsers,
      Pending: pendingUsers,
      SuperAdmin: filteredUsers.filter((user) => (currentRolesByUser[user.id] ?? []).includes("SuperAdmin")),
      Commish: filteredUsers.filter((user) => (currentRolesByUser[user.id] ?? []).includes("Commish")),
      Coach: filteredUsers.filter((user) => (currentRolesByUser[user.id] ?? []).includes("Coach")),
      Parent: filteredUsers.filter((user) => (currentRolesByUser[user.id] ?? []).includes("Parent"))
    } as Record<UserTab, AppUser[]>;
  }, [cognitoStateByEmail, cognitoStateByUser, currentRolesByUser, filteredUsers]);

  const activeUsers = usersByTab[activeTab] ?? [];

  const summaryCards = useMemo(() => {
    const confirmedCount = users.filter((user) => {
      const state = resolveCognitoState(user);
      return state?.enabled !== false && state?.userStatus === "CONFIRMED";
    }).length;
    const pendingCount = users.filter((user) => {
      const state = resolveCognitoState(user);
      return !state || state.enabled === false || state.userStatus !== "CONFIRMED";
    }).length;
    const coachCount = users.filter((user) => (currentRolesByUser[user.id] ?? []).includes("Coach")).length;
    const parentCount = users.filter((user) => (currentRolesByUser[user.id] ?? []).includes("Parent")).length;
    return [
      { label: "Total users", value: String(users.length), hint: "App profiles in PitchTracker." },
      { label: "Confirmed", value: String(confirmedCount), hint: "Cognito accounts ready to sign in." },
      { label: "Pending", value: String(pendingCount), hint: "Pending, disabled, or profile-only users." },
      { label: "Parents", value: String(parentCount), hint: "Users who can act as parents." },
      { label: "Coaches", value: String(coachCount), hint: "Users who can act as coaches." }
    ];
  }, [cognitoStateByEmail, cognitoStateByUser, currentRolesByUser, users]);

  useEffect(() => {
    if ((usersByTab[activeTab] ?? []).length > 0 || activeTab === "All") {
      return;
    }
    setActiveTab("All");
  }, [activeTab, usersByTab]);

  const syncRolesToCognito = async (username: string, currentRoles: string[], desiredRoles: string[]) => {
    const normalizedDesiredRoles = normalizeRoles(desiredRoles);
    const normalizedCurrentRoles = normalizeRoles(currentRoles);
    if (normalizedDesiredRoles.length === 0) {
      throw new Error("At least one role must be selected.");
    }

    const removableGroups = normalizedCurrentRoles.filter((role) => !normalizedDesiredRoles.includes(role));
    const addableGroups = normalizedDesiredRoles.filter((role) => !normalizedCurrentRoles.includes(role));

    for (const groupname of removableGroups) {
      try {
        await adminPost("/removeUserFromGroup", { username, groupname });
      } catch (error) {
        if (!shouldIgnoreGroupSyncError(error, "remove")) {
          throw error;
        }
      }
    }

    for (const groupname of addableGroups) {
      try {
        await adminPost("/addUserToGroup", { username, groupname });
      } catch (error) {
        if (!shouldIgnoreGroupSyncError(error, "add")) {
          throw error;
        }
      }
    }
  };

  const saveProfile = async (username: string) => {
    const draft = profileDrafts[username];
    const existingUser = appUsersById[username];
    if (!draft || !existingUser) {
      return;
    }

    setSavingUser(username);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const roles = normalizeRoles(selectedRoles[username] ?? currentRolesByUser[username] ?? ["Parent"]);
      const cognitoUsername = resolveCognitoUsername(existingUser);
      await syncRolesToCognito(cognitoUsername, existingUser.roles ?? [], roles);

      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: username,
            email: existingUser.email,
            givenName: draft.givenName || null,
            familyName: draft.familyName || null,
            phone: draft.phone || null,
            safetyCertified: draft.safetyCertified,
            abcaId: draft.abcaId || null,
            roles
          }
        })
      );

      setSuccessMessage(`Saved profile for ${username}.`);
      setEditingUsername(null);
      await loadUsers();
    } catch (error) {
      setErrorMessage(`Failed to save profile for ${username}: ${formatApiError(error)}`);
    } finally {
      setSavingUser("");
    }
  };

  const createNewUser = async () => {
    const username = createDraft.username.trim();
    const email = createDraft.email.trim().toLowerCase();
    const roles = normalizeRoles(createDraft.roles);

    if (!username || !email) {
      setErrorMessage("Username and email are required to create a user.");
      return;
    }
    if (roles.length === 0) {
      setErrorMessage("Select at least one role for the new user.");
      return;
    }

    setCreatingUser(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const userAttributes = [
        { Name: "email", Value: email },
        ...(createDraft.givenName.trim() ? [{ Name: "given_name", Value: createDraft.givenName.trim() }] : []),
        ...(createDraft.familyName.trim() ? [{ Name: "family_name", Value: createDraft.familyName.trim() }] : []),
        ...(createDraft.phone.trim() ? [{ Name: "phone_number", Value: createDraft.phone.trim() }] : [])
      ];

      await adminPost("/createUser", {
        username,
        email,
        userAttributes
      });

      await syncRolesToCognito(username, [], roles);

      await API.graphql(
        graphqlOperation(createUser, {
          input: {
            id: username,
            email,
            givenName: createDraft.givenName.trim() || null,
            familyName: createDraft.familyName.trim() || null,
            phone: createDraft.phone.trim() || null,
            safetyCertified: createDraft.safetyCertified,
            abcaId: createDraft.abcaId.trim() || null,
            roles
          }
        })
      );

      setCreateDialogOpen(false);
      setCreateDraft(emptyCreateDraft());
      setSuccessMessage(`Created ${username} and sent the Cognito invite email.`);
      await loadUsers();
    } catch (error) {
      setErrorMessage(`Failed to create user: ${formatApiError(error)}`);
    } finally {
      setCreatingUser(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const editingDraft = editingUsername ? profileDrafts[editingUsername] : undefined;
  const editingUser = editingUsername ? appUsersById[editingUsername] : undefined;
  const editingCurrentRoles = editingUsername ? currentRolesByUser[editingUsername] ?? ["Parent"] : ["Parent"];
  const editingSelectedRoles = editingUsername ? selectedRoles[editingUsername] ?? editingCurrentRoles : ["Parent"];
  const detailUser = detailUsername ? appUsersById[detailUsername] : undefined;
  const detailDraft = detailUsername ? profileDrafts[detailUsername] : undefined;
  const detailLinked = detailUsername ? linkedObjectsByUser[detailUsername] : undefined;
  const detailRoles = detailUsername ? currentRolesByUser[detailUsername] ?? ["Parent"] : ["Parent"];
  const detailStatus = detailUsername && detailUser ? getStatusLabel(resolveCognitoState(detailUser)) : "";

  return (
    <div className="user-management-page max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Users</h1>
      <p className="text-gray-600 mb-6">
        Manage user profiles from the app record first, and use Cognito only for invite and sign-in account status.
      </p>

      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}
      {warningMessage && <p className="mb-4" style={{ color: "#92400e" }}>{warningMessage}</p>}
      {successMessage && <p className="mb-4" style={{ color: "#166534" }}>{successMessage}</p>}

      {loading ? (
        <div className="bubble-card p-4">Loading users...</div>
      ) : (
        <>
          <div className="bubble-card p-4 user-toolbar">
            <div>
              <h2>Directory</h2>
              <p className="text-sm text-gray-600">Admins can invite people, edit profiles, and keep app roles aligned with sign-in access.</p>
            </div>
            <div className="user-toolbar__actions">
              <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" onClick={() => setCreateDialogOpen(true)}>
                Create User
              </button>
              <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700" onClick={loadUsers}>
                Refresh
              </button>
            </div>
          </div>

          <div className="user-summary-grid">
            {summaryCards.map((card) => (
              <div key={card.label} className="bubble-card p-4 user-summary-card">
                <div className="user-summary-card__value">{card.value}</div>
                <div className="user-summary-card__label">{card.label}</div>
                <div className="user-summary-card__hint">{card.hint}</div>
              </div>
            ))}
          </div>

          <div className="bubble-card p-4 user-filter-grid">
            <input
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              className="border px-3 py-2 rounded"
              placeholder="Search name, username, email, phone, ABCA, role"
            />
            <select value={filterRole} onChange={(event) => setFilterRole(event.target.value)} className="border px-3 py-2 rounded">
              <option value="">All roles</option>
              {managedRoles.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            <select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)} className="border px-3 py-2 rounded">
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <section className="bubble-card p-4">
            <div className="user-section-header">
              <div>
                <h2>People</h2>
                <p className="text-sm text-gray-600">Role tabs on top, dense directory table below.</p>
              </div>
              <span className="bubble-pill">{activeUsers.length}</span>
            </div>

            <div className="user-tab-row" role="tablist" aria-label="User role tabs">
              {userTabs.map((tab) => {
                const count = usersByTab[tab]?.length ?? 0;
                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab}
                    className={`user-tab ${activeTab === tab ? "user-tab--active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    <span>{tab}</span>
                    <span className="user-tab__count">{count}</span>
                  </button>
                );
              })}
            </div>

            {activeUsers.length === 0 ? (
              <p className="text-gray-600 user-empty-state">No users match this tab right now.</p>
            ) : (
              <div className="user-table-wrapper">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Roles</th>
                      <th>Status</th>
                      <th>Phone</th>
                      <th>Flags</th>
                      <th>Links</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.map((user) => {
                      const draft = profileDrafts[user.id];
                      const linked = linkedObjectsByUser[user.id] ?? { teams: [], organizations: [], leagues: [], players: [] };
                      const roles = currentRolesByUser[user.id] ?? ["Parent"];
                      const status = getStatusLabel(cognitoStateByUser[user.id]);
                      return (
                        <tr key={user.id}>
                          <td>
                            <button className="user-table__nameButton" onClick={() => setDetailUsername(user.id)}>
                              <div className="user-table__name">{getDisplayName(user, draft)}</div>
                            </button>
                            <div className="user-table__sub">@{user.id}</div>
                            <div className="user-table__sub">{user.email || "No email on file"}</div>
                          </td>
                          <td>
                            <div className="bubble-pill-wrap">
                              {roles.map((role) => (
                                <span key={role} className="bubble-pill">{role}</span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <span className="bubble-pill">{status}</span>
                          </td>
                          <td>{draft?.phone || "None"}</td>
                          <td>
                            <div className="bubble-pill-wrap">
                              {draft?.safetyCertified ? <span className="bubble-pill">Safety</span> : null}
                              {draft?.abcaId ? <span className="bubble-pill">ABCA {draft.abcaId}</span> : <span className="user-table__muted">None</span>}
                            </div>
                          </td>
                          <td>
                            <button className="user-linkButton" onClick={() => setDetailUsername(user.id)}>
                              {linked.teams.length + linked.organizations.length + linked.leagues.length + linked.players.length} details
                            </button>
                          </td>
                          <td className="user-table__actions">
                            <button className="user-card__edit user-card__edit--secondary" onClick={() => setDetailUsername(user.id)}>
                              Details
                            </button>
                            <button
                              className="user-card__edit"
                              onClick={() => {
                                setEditingUsername(user.id);
                                setSelectedRoles((current) => ({
                                  ...current,
                                  [user.id]: current[user.id] ?? roles
                                }));
                              }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      <AppDialog
        open={createDialogOpen}
        title="Create User"
        onClose={() => {
          if (!creatingUser) {
            setCreateDialogOpen(false);
          }
        }}
        footer={
          <>
            <button className="bg-gray-200 px-4 py-2 rounded" onClick={() => setCreateDialogOpen(false)} disabled={creatingUser}>
              Cancel
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={createNewUser} disabled={creatingUser}>
              {creatingUser ? "Creating..." : "Create User"}
            </button>
          </>
        }
      >
        <div className="user-dialog-grid">
          <input
            className="border px-3 py-2 rounded w-full"
            placeholder="Username"
            value={createDraft.username}
            onChange={(event) => setCreateDraft((current) => ({ ...current, username: event.target.value }))}
          />
          <input
            className="border px-3 py-2 rounded w-full"
            placeholder="Email"
            type="email"
            value={createDraft.email}
            onChange={(event) => setCreateDraft((current) => ({ ...current, email: event.target.value }))}
          />
          <input
            className="border px-3 py-2 rounded w-full"
            placeholder="First name"
            value={createDraft.givenName}
            onChange={(event) => setCreateDraft((current) => ({ ...current, givenName: event.target.value }))}
          />
          <input
            className="border px-3 py-2 rounded w-full"
            placeholder="Last name"
            value={createDraft.familyName}
            onChange={(event) => setCreateDraft((current) => ({ ...current, familyName: event.target.value }))}
          />
          <input
            className="border px-3 py-2 rounded w-full"
            placeholder="Phone"
            value={createDraft.phone}
            onChange={(event) => setCreateDraft((current) => ({ ...current, phone: event.target.value }))}
          />
          <input
            className="border px-3 py-2 rounded w-full"
            placeholder="ABCA ID"
            value={createDraft.abcaId}
            onChange={(event) => setCreateDraft((current) => ({ ...current, abcaId: event.target.value }))}
          />
          <label className="text-sm text-gray-700 inline-flex items-center gap-2 px-2">
            <input
              type="checkbox"
              checked={createDraft.safetyCertified}
              onChange={(event) => setCreateDraft((current) => ({ ...current, safetyCertified: event.target.checked }))}
            />
            Safety Certified
          </label>
        </div>
        <div className="user-role-picker">
          <div className="user-role-picker__label">Roles</div>
          <div className="user-role-picker__grid">
            {managedRoles.map((role) => (
              <label key={role} className="user-role-picker__item">
                <input
                  type="checkbox"
                  checked={createDraft.roles.includes(role)}
                  onChange={(event) =>
                    setCreateDraft((current) => ({
                      ...current,
                      roles: event.target.checked
                        ? normalizeRoles([...current.roles, role])
                        : current.roles.filter((candidate) => candidate !== role)
                    }))
                  }
                />
                <span>{role}</span>
              </label>
            ))}
          </div>
        </div>
        <p className="text-sm text-gray-600 user-dialog-note">
          This creates the Cognito account for sign-in, then creates the PitchTracker user profile with the selected roles.
        </p>
      </AppDialog>

      <AppDialog
        open={Boolean(detailUsername && detailUser && detailDraft && detailLinked)}
        title="User Details"
        onClose={() => setDetailUsername(null)}
        footer={
          <>
            <button className="bg-gray-200 px-4 py-2 rounded" onClick={() => setDetailUsername(null)}>
              Close
            </button>
            {detailUsername ? (
              <button
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                onClick={() => {
                  setSelectedRoles((current) => ({
                    ...current,
                    [detailUsername]: current[detailUsername] ?? detailRoles
                  }));
                  setEditingUsername(detailUsername);
                  setDetailUsername(null);
                }}
              >
                Edit User
              </button>
            ) : null}
          </>
        }
      >
        {detailUsername && detailUser && detailDraft && detailLinked ? (
          <div className="user-detail-modal">
            <div className="user-detail-modal__hero">
              <div>
                <div className="user-table__name">{getDisplayName(detailUser, detailDraft)}</div>
                <div className="user-table__sub">@{detailUsername}</div>
                <div className="user-table__sub">{detailUser.email || "No email on file"}</div>
              </div>
              <div className="bubble-pill-wrap">
                {detailRoles.map((role) => (
                  <span key={role} className="bubble-pill">{role}</span>
                ))}
                <span className="bubble-pill">{detailStatus}</span>
              </div>
            </div>

            <div className="user-detail-modal__grid">
              <div className="user-detail-modal__section">
                <h3>Profile</h3>
                <div className="user-detail-modal__rows">
                  <div><span>Phone</span><strong>{detailDraft.phone || "None"}</strong></div>
                  <div><span>ABCA ID</span><strong>{detailDraft.abcaId || "None"}</strong></div>
                  <div><span>Safety Certified</span><strong>{detailDraft.safetyCertified ? "Yes" : "No"}</strong></div>
                </div>
              </div>

              <div className="user-detail-modal__section">
                <h3>Assignments</h3>
                <div className="user-detail-modal__rows">
                  <div><span>Teams</span><strong>{summarizeNames(detailLinked.teams, 6)}</strong></div>
                  <div><span>Organizations</span><strong>{summarizeNames(detailLinked.organizations, 6)}</strong></div>
                  <div><span>Leagues</span><strong>{summarizeNames(detailLinked.leagues, 6)}</strong></div>
                  <div><span>Players</span><strong>{summarizeNames(detailLinked.players, 6)}</strong></div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </AppDialog>

      <AppDialog
        open={Boolean(editingUsername && editingDraft && editingUser)}
        title="Edit User"
        onClose={() => {
          if (!savingUser) {
            setEditingUsername(null);
          }
        }}
        footer={
          <>
            <button className="bg-gray-200 px-4 py-2 rounded" onClick={() => setEditingUsername(null)} disabled={Boolean(savingUser)}>
              Close
            </button>
            {editingUsername ? (
              <button
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                onClick={() => saveProfile(editingUsername)}
                disabled={savingUser === editingUsername}
              >
                {savingUser === editingUsername ? "Saving..." : "Save Changes"}
              </button>
            ) : null}
          </>
        }
      >
        {editingUsername && editingDraft && editingUser ? (
          <>
            <div className="user-dialog-identity">
              <div>
                <div className="user-dialog-identity__label">Username</div>
                <div className="user-dialog-identity__value">{editingUsername}</div>
              </div>
              <div>
                <div className="user-dialog-identity__label">Email</div>
                <div className="user-dialog-identity__value">{editingUser.email || "No email"}</div>
              </div>
            </div>

            <div className="user-dialog-grid">
              <input
                className="border px-3 py-2 rounded w-full"
                placeholder="First name"
                value={editingDraft.givenName}
                onChange={(event) =>
                  setProfileDrafts((prev) => ({
                    ...prev,
                    [editingUsername]: { ...prev[editingUsername], givenName: event.target.value }
                  }))
                }
              />
              <input
                className="border px-3 py-2 rounded w-full"
                placeholder="Last name"
                value={editingDraft.familyName}
                onChange={(event) =>
                  setProfileDrafts((prev) => ({
                    ...prev,
                    [editingUsername]: { ...prev[editingUsername], familyName: event.target.value }
                  }))
                }
              />
              <input
                className="border px-3 py-2 rounded w-full"
                placeholder="Phone"
                value={editingDraft.phone}
                onChange={(event) =>
                  setProfileDrafts((prev) => ({
                    ...prev,
                    [editingUsername]: { ...prev[editingUsername], phone: event.target.value }
                  }))
                }
              />
              <input
                className="border px-3 py-2 rounded w-full"
                placeholder="ABCA ID"
                value={editingDraft.abcaId}
                onChange={(event) =>
                  setProfileDrafts((prev) => ({
                    ...prev,
                    [editingUsername]: { ...prev[editingUsername], abcaId: event.target.value }
                  }))
                }
              />
              <label className="text-sm text-gray-700 inline-flex items-center gap-2 px-2">
                <input
                  type="checkbox"
                  checked={editingDraft.safetyCertified}
                  onChange={(event) =>
                    setProfileDrafts((prev) => ({
                      ...prev,
                      [editingUsername]: { ...prev[editingUsername], safetyCertified: event.target.checked }
                    }))
                  }
                />
                Safety Certified
              </label>
            </div>
            <div className="user-role-picker">
              <div className="user-role-picker__label">Roles</div>
              <div className="user-role-picker__grid">
                {managedRoles.map((role) => (
                  <label key={role} className="user-role-picker__item">
                    <input
                      type="checkbox"
                      checked={editingSelectedRoles.includes(role)}
                      onChange={(event) =>
                        setSelectedRoles((prev) => ({
                          ...prev,
                          [editingUsername]: event.target.checked
                            ? normalizeRoles([...(prev[editingUsername] ?? editingCurrentRoles), role])
                            : (prev[editingUsername] ?? editingCurrentRoles).filter((candidate) => candidate !== role)
                        }))
                      }
                    />
                    <span>{role}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </AppDialog>
    </div>
  );
};

export default UserManagementPage;
